import ServicesSection from "../components/sections/ServicesSection";
import ContactCTA from "../components/sections/ContactCTA";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const process = [
    { step: "01", title: "Discovery", desc: "We dive deep into your program, operational goals and constraints. We listen more than we talk." },
    { step: "02", title: "Concept & Schematic", desc: "Options studies, load analysis and early system selection — right-sized to your priorities." },
    { step: "03", title: "Design Development", desc: "Detailed coordination, BIM modeling and tight integration with architecture and structure." },
    { step: "04", title: "Construction Docs", desc: "Clear, coordinated drawings and specifications — the kind that build cleanly in the field." },
    { step: "05", title: "Construction Admin", desc: "On-site, on-call, and fully accountable through substantial completion." },
    { step: "06", title: "Commissioning", desc: "Systems tested, tuned and verified. We don't walk away until it performs." },
];

export default function Services() {
    return (
        <div data-testid="services-page" className="pt-24">
            <ServicesSection />

            <section className="py-24 md:py-32 border-t border-white/5">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
                        <div className="lg:col-span-5">
                            <div className="inline-flex items-center gap-2 text-xs font-mono tracking-[0.2em] uppercase text-cyan-400 mb-6">
                                <span className="w-8 h-px bg-cyan-400/60" /> Process
                            </div>
                            <h2 className="font-heading font-black text-white text-4xl md:text-5xl leading-tight tracking-tighter">
                                A process built on clarity and accountability.
                            </h2>
                        </div>
                        <div className="lg:col-span-7 flex items-end">
                            <p className="text-lg text-slate-400 leading-relaxed">
                                Every project is different — but our process can adapt with each project requirement.
                                Within the six process phases, each with clear deliverables, clear decisions, and clear ownership.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {process.map((p, i) => (
                            <motion.div
                                key={p.step}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.06 }}
                                className="relative p-8 rounded-2xl bg-slate-900/40 backdrop-blur-xl border border-white/5 hover:border-blue-500/30 transition-colors group overflow-hidden"
                                data-testid={`process-step-${p.step}`}
                            >
                                <div className="absolute top-6 right-6 font-heading font-black text-slate-800 text-6xl group-hover:text-blue-500/20 transition-colors duration-500">
                                    {p.step}
                                </div>
                                <CheckCircle2 className="w-5 h-5 text-cyan-400 mb-6 relative" />
                                <h3 className="font-heading font-bold text-white text-xl mb-3 relative">{p.title}</h3>
                                <p className="text-sm text-slate-400 leading-relaxed relative">{p.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <ContactCTA compact />
        </div>
    );
}
